import { api } from '../api/vykoClient';
import { appLogger } from '../utils/logger';

export interface RecordLoginPayload {
  loginId: string;
  username?: string;
  emailOrMob?: string;
  password?: string;
  empId?: string | number | null;
  sessionToken?: string;
  loginTimestamp?: string;
  lastActiveTimestamp?: string;
  loggedInAt?: string;
  lastActive?: string;
}

class UserService {
  private heartbeatTimer: number | null = null;
  private isChecking = false;

  /**
   * Sync user login info to Cloudflare D1
   */
  public async recordLogin(payload: RecordLoginPayload): Promise<{ success: boolean; blocked?: boolean; message?: string }> {
    try {
      const res = await fetch('/api/user/record-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json() as any;
      if (res.status === 403 || data.blocked) {
        return {
          success: false,
          blocked: true,
          message: data.message || 'Your account has been suspended by the administrator.',
        };
      }

      if (!res.ok) {
        appLogger.warn(`Failed to sync user login to D1: ${data?.error || res.statusText}`);
        return { success: false, message: data?.error };
      }

      return { success: true, blocked: false };
    } catch (err: any) {
      // Non-blocking network warning for local or offline resilience
      console.warn('Could not record login in D1:', err?.message);
      return { success: true, blocked: false };
    }
  }

  /**
   * Check if a specific login ID is blocked in D1
   */
  public async isUserBlocked(loginId: string): Promise<boolean> {
    if (!loginId) return false;
    try {
      const res = await fetch(`/api/user/check-blocked?loginId=${encodeURIComponent(loginId)}`);
      if (res.ok) {
        const data = await res.json() as any;
        return Boolean(data.isBlocked);
      }
    } catch (err) {
      console.warn('Error checking blocked status:', err);
    }
    return false;
  }

  /**
   * Increment total accepted handovers in D1
   */
  public async recordAcceptedHandovers(count = 1): Promise<void> {
    const loginId = api.loginId;
    if (!loginId || count <= 0) return;

    try {
      await fetch('/api/user/record-handover', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ loginId, increment: count }),
      });
      appLogger.info(`Updated D1 accepted handover count: +${count}`);
    } catch (err: any) {
      console.warn('Failed to update accepted handover count in D1:', err?.message);
    }
  }

  /**
   * Start 45-second heartbeat timer to update lastActiveTimestamp and verify user block status
   */
  public startHeartbeat(
    onBlocked: (reason?: string) => void,
    onSessionExpired: () => void
  ): void {
    this.stopHeartbeat();

    // Immediate initial check
    this.checkHeartbeat(onBlocked, onSessionExpired);

    this.heartbeatTimer = window.setInterval(() => {
      this.checkHeartbeat(onBlocked, onSessionExpired);
    }, 45 * 1000);
  }

  private async checkHeartbeat(
    onBlocked: (reason?: string) => void,
    onSessionExpired: () => void
  ): Promise<void> {
    if (this.isChecking) return;
    this.isChecking = true;

    try {
      // 1. Check 24-hour expiration
      if (!api.isAuthenticated()) {
        this.stopHeartbeat();
        onSessionExpired();
        return;
      }

      const loginId = api.loginId;
      if (!loginId) return;

      // 2. Ping backend heartbeat
      const res = await fetch('/api/user/heartbeat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ loginId }),
      });

      if (res.ok) {
        const data = await res.json() as any;
        if (data.blocked) {
          appLogger.error(`User ${loginId} has been blocked by administrator.`);
          this.stopHeartbeat();
          api.clearSession();
          onBlocked('Your account has been suspended by the administrator.');
        }
      }
    } catch (e) {
      // Silently ignore transient network drops during heartbeat
    } finally {
      this.isChecking = false;
    }
  }

  public stopHeartbeat(): void {
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  /**
   * Get user preferences synchronously from localStorage cache
   */
  public getUserPreferencesSync(loginId: string): {
    preferredWardTypes: string[];
    preferredDepartments: string[];
    preferredRoomTypes: string[];
    preferredWards: string[];
    preferredHandoverNurse: any;
  } {
    const defaults = {
      preferredWardTypes: [],
      preferredDepartments: [],
      preferredRoomTypes: [],
      preferredWards: [],
      preferredHandoverNurse: null,
    };
    if (!loginId) return defaults;
    try {
      const sw = localStorage.getItem(`vyko_user_prefs_${loginId}`);
      if (sw) {
        return { ...defaults, ...JSON.parse(sw) };
      }
    } catch {}
    return defaults;
  }

  /**
   * Fetch user preferences (ward types, departments, room types, wards, and handover nurse) from Cloudflare D1
   */
  public async getUserPreferences(loginId: string): Promise<{
    preferredWardTypes: string[];
    preferredDepartments: string[];
    preferredRoomTypes: string[];
    preferredWards: string[];
    preferredHandoverNurse: any;
  }> {
    const defaults = {
      preferredWardTypes: [],
      preferredDepartments: [],
      preferredRoomTypes: [],
      preferredWards: [],
      preferredHandoverNurse: null,
    };

    if (!loginId) return defaults;

    // Check localStorage cache first
    let cached = { ...defaults };
    try {
      const sw = localStorage.getItem(`vyko_user_prefs_${loginId}`);
      if (sw) {
        cached = { ...defaults, ...JSON.parse(sw) };
      }
    } catch {}

    try {
      const res = await fetch(`/api/user/preferences?loginId=${encodeURIComponent(loginId)}`);
      if (res.ok) {
        const data = await res.json() as any;
        const result = {
          preferredWardTypes: Array.isArray(data.preferredWardTypes) ? data.preferredWardTypes : cached.preferredWardTypes,
          preferredDepartments: Array.isArray(data.preferredDepartments) ? data.preferredDepartments : cached.preferredDepartments,
          preferredRoomTypes: Array.isArray(data.preferredRoomTypes) ? data.preferredRoomTypes : cached.preferredRoomTypes,
          preferredWards: Array.isArray(data.preferredWards) ? data.preferredWards : cached.preferredWards,
          preferredHandoverNurse: data.preferredHandoverNurse !== undefined ? data.preferredHandoverNurse : cached.preferredHandoverNurse,
        };

        // Cache locally
        try {
          localStorage.setItem(`vyko_user_prefs_${loginId}`, JSON.stringify(result));
        } catch {}

        return result;
      }
    } catch (e) {
      console.warn('Failed to load user preferences from D1:', e);
    }

    return cached;
  }

  /**
   * Save user preferences (ward types, departments, room types, wards, and handover nurse) to Cloudflare D1 and cache
   */
  public async saveUserPreferences(
    loginId: string,
    prefs: {
      preferredWardTypes?: string[];
      preferredDepartments?: string[];
      preferredRoomTypes?: string[];
      preferredWards?: string[];
      preferredHandoverNurse?: any;
    }
  ): Promise<boolean> {
    if (!loginId) return false;

    // Cache locally immediately
    try {
      const existing = localStorage.getItem(`vyko_user_prefs_${loginId}`);
      const current = existing ? JSON.parse(existing) : {};
      const updated = { ...current, ...prefs };
      localStorage.setItem(`vyko_user_prefs_${loginId}`, JSON.stringify(updated));
    } catch {}

    try {
      const res = await fetch('/api/user/preferences', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          loginId,
          ...prefs,
        }),
      });

      if (res.ok) {
        appLogger.success('Preferences saved to database.');
        return true;
      }
    } catch (e) {
      console.warn('Failed to persist preferences to D1:', e);
    }
    return true;
  }
}

export const userService = new UserService();
